// @ts-check
import React from 'react';

import Paper from '@liquidweb-dev/undercurrent/Paper';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import HTMLContent from 'components/HTMLContent';
import TextWithLineBreaks from 'components/legacy/TextWithLineBreaks';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import Avatar from '@liquidweb-dev/undercurrent/Avatar';
import ClockIcon from '@liquidweb-dev/undercurrent/icons/lucide/Clock';
import ReplyForm from './ReplyForm';

dayjs.extend(localizedFormat);

/** @param {{ children: React.ReactNode }} props */
const MessagePaper = ({ children }) => {
	return (
		<Paper
			elevation={1}
			sx={{
				padding: 200,
				display: 'flex',
				flexDirection: 'column',
				gap: 200,

				overflowWrap: 'anywhere',
				img: {
					display: 'block',
					maxWidth: '100%',
				},
			}}
		>
			{children}
		</Paper>
	);
};

/** @param {{ date: string, sx?: import('@liquidweb-dev/undercurrent/Stack').StackProps['sx'] }} props */
const Timestamp = ({ date, sx }) => {
	return (
		<Stack
			direction="row"
			sx={[
				{
					gap: 50,
					alignItems: 'center',
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			<ClockIcon />
			<Typography
				variant="label-md"
				sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
			>
				{dayjs(date).format('l LTS')}
			</Typography>
		</Stack>
	);
};

/** @param {{ children: React.ReactNode }} props */
const Wrapper = ({ children }) => (
	<Stack
		sx={{
			gap: 200,
			container: 'support-conversation',
			containerType: 'inline-size',
		}}
	>
		<Typography
			variant="title-xs"
			sx={{
				fontWeight: 500,
			}}
		>
			Conversation
		</Typography>
		{children}
	</Stack>
);

/**
 * @param {object} props
 * @param {import('@liquidweb-dev/club-sauce/public/support/request/index.raw').LWApiPublicSupportRequestDetailsResultRawI['id']} props.caseId
 * @param {import('@liquidweb-dev/club-sauce/public/support/request/index.raw').LWApiPublicSupportRequestDetailsResultRawI['comments']} props.comments
 * @param {import('@liquidweb-dev/club-sauce/public/support/request/index.raw').LWApiPublicSupportRequestDetailsResultRawI['created_date']} props.createdDate
 * @param {import('@liquidweb-dev/club-sauce/public/support/request/index.raw').LWApiPublicSupportRequestDetailsResultRawI['customComments']} props.customComments
 * @param {import('@liquidweb-dev/club-sauce/public/support/request/index.raw').LWApiPublicSupportRequestDetailsResultRawI['emailsWithAttachmentDetails']} props.emailsWithAttachmentDetails
 * @param {import('@liquidweb-dev/club-sauce/public/support/request/index.raw').LWApiPublicSupportRequestDetailsResultRawI['description']} props.description
 * @param {boolean} props.respondable
 * @param {() => void} [props.onCancelReply]
 * @param {boolean} [props.isLoading]
 */
const SupportConversation = ({
	caseId,
	comments = [],
	createdDate,
	customComments = [],
	emailsWithAttachmentDetails = [],
	description,
	respondable,
	onCancelReply,
	isLoading,
}) => {
	if (isLoading) {
		return (
			<Wrapper>
				<Skeleton variant="rounded" width="100%" height="134px" />
				<Stack
					direction="row"
					sx={{
						gap: 100,
					}}
				>
					<Skeleton
						variant="rounded"
						height="40px"
						sx={{
							width: {
								'@500/support-conversation': '70px',
								'@0/support-conversation': '100%',
							},
							flexGrow: {
								'@500/support-conversation': 0,
								'@0/support-conversation': 1,
							},
						}}
					/>
					<Skeleton
						variant="rounded"
						height="40px"
						sx={{
							width: {
								'@500/support-conversation': '70px',
								'@0/support-conversation': '100%',
							},
							flexGrow: {
								'@500/support-conversation': 0,
								'@0/support-conversation': 1,
							},
						}}
					/>
				</Stack>
				<Skeleton variant="rounded" width="100%" height="200px" />
			</Wrapper>
		);
	}

	const commentsFormatted = comments.map(
		({ created_date: date, comment, id: commentId }) => ({
			htmlBody: '',
			body: comment,
			date: dayjs(date).format('l LTS'),
			dateRaw: date,
			id: commentId,
			user: '',
		}),
	);

	const customCommentsFormatted = customComments.map(
		({ id: commentId, body, contact_email: email, created_date: date }) => ({
			htmlBody: '',
			body,
			date: dayjs(date).format('l LTS'),
			dateRaw: date,
			id: commentId,
			user: email,
		}),
	);

	const emailsFormatted = emailsWithAttachmentDetails.map(
		({
			html_body: htmlBody,
			body,
			message_date: date,
			id: emailId,
			from_address: from,
		}) => ({
			htmlBody,
			body,
			date: dayjs(date).format('l LTS'),
			dateRaw: date,
			id: emailId,
			user: from,
		}),
	);

	const allComments = [
		...commentsFormatted,
		...customCommentsFormatted,
		...emailsFormatted,
	].sort((a, b) => {
		return dayjs(b.dateRaw).diff(dayjs(a.dateRaw));
	});

	return (
		<Wrapper>
			{respondable && caseId && (
				<ReplyForm onCancel={onCancelReply} caseId={caseId} />
			)}
			{allComments.map(({ body, htmlBody, date, user, id }) => (
				<MessagePaper key={id}>
					<Stack
						direction="row"
						sx={{
							justifyContent: 'space-between',
							alignItems: 'center',
						}}
					>
						<Stack
							direction="row"
							sx={{
								gap: 150,
								alignItems: 'center',
							}}
						>
							{user && <Avatar sx={{ margin: 0 }}>{user[0]}</Avatar>}
							<Typography
								variant="label-xl"
								sx={{
									fontWeight: 500,
								}}
							>
								{user}
							</Typography>
						</Stack>

						<Timestamp
							date={date}
							sx={{
								display: {
									'@500/support-conversation': 'flex',
									'@0/support-conversation': 'none',
								},
							}}
						/>
					</Stack>

					<Typography variant="body-md">
						{htmlBody ? (
							<HTMLContent content={htmlBody} />
						) : (
							<TextWithLineBreaks>{body}</TextWithLineBreaks>
						)}
					</Typography>

					<Timestamp
						date={date}
						sx={{
							display: {
								'@500/support-conversation': 'none',
								'@0/support-conversation': 'flex',
							},
						}}
					/>
				</MessagePaper>
			))}
			{description && (
				<MessagePaper>
					<Stack
						direction="row"
						sx={{
							justifyContent: 'space-between',
							alignItems: 'center',
						}}
					>
						<Typography
							variant="label-xl"
							sx={{
								fontWeight: 500,
							}}
						>
							Case Description
						</Typography>

						{createdDate && (
							<Timestamp
								date={createdDate}
								sx={{
									display: {
										'@500/support-conversation': 'flex',
										'@0/support-conversation': 'none',
									},
								}}
							/>
						)}
					</Stack>

					<Typography variant="body-md">
						<TextWithLineBreaks>{description}</TextWithLineBreaks>
					</Typography>

					{createdDate && (
						<Timestamp
							date={createdDate}
							sx={{
								display: {
									'@500/support-conversation': 'none',
									'@0/support-conversation': 'flex',
								},
							}}
						/>
					)}
				</MessagePaper>
			)}
		</Wrapper>
	);
};

export default SupportConversation;
